import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import VariantManager from "@/components/admin/VariantManager";

const EMPTY = { slug: "", name: "", chemical_name: "", category: "Recovery", focus: "", sequence: "", purity: "≥ 98%", form: "Lyophilized powder", molecular_weight: "", summary: "", benefits: [], dosage: "", storage: "", featured: false, price: 0, status: "active", image_url: "", variants: [], recommended_products: [] };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [benefitsRaw, setBenefitsRaw] = useState("");

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, variants: [] }); setBenefitsRaw(""); setEditing(null); setOpen(true); };
  const openEdit = (p) => {
    // Only surface currently-active variants to the editor. Soft-deleted ones stay in the DB and
    // are re-attached on the backend during the PATCH merge.
    const activeVariants = (p.variants || []).filter((v) => v.active !== false).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setForm({ ...EMPTY, ...p, variants: activeVariants });
    setBenefitsRaw((p.benefits || []).join("\n"));
    setEditing(p.slug);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const variants = (form.variants || []).map((v, i) => {
      const clean = { ...v };
      // Strip temporary frontend IDs so the backend assigns a real one.
      if (typeof clean.id === "string" && clean.id.startsWith("new_")) delete clean.id;
      clean.price = Number(clean.price || 0);
      clean.inventory = Number(clean.inventory || 0);
      clean.sort_order = i;
      return clean;
    });
    // Validation: at least one variant, one default, labels present, no dup labels.
    if (variants.length === 0) { toast.error("Add at least one dose variant"); return; }
    if (variants.some((v) => !v.label || !v.label.trim())) { toast.error("Every variant needs a label"); return; }
    const labels = variants.map((v) => v.label.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) { toast.error("Variant labels must be unique"); return; }
    if (!variants.some((v) => v.default && v.active !== false)) {
      // Auto-promote the first active variant.
      const idx = variants.findIndex((v) => v.active !== false);
      if (idx < 0) { toast.error("At least one variant must be active"); return; }
      variants.forEach((v, i) => { v.default = i === idx; });
    }
    const body = {
      ...form,
      price: Number(variants.find((v) => v.default)?.price ?? form.price ?? 0),
      benefits: benefitsRaw.split("\n").map(x => x.trim()).filter(Boolean),
      variants,
    };
    try {
      if (editing) await api.patch(`/admin/products/${editing}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const archive = async (slug) => {
    if (!window.confirm("Archive this product? It will disappear from the public catalog.")) return;
    await api.delete(`/admin/products/${slug}`);
    toast.success("Archived"); load();
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Product Management</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Products</h1>
        </div>
        <Button data-testid="admin-add-product" onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md"><Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> New product</Button>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Product</th>
              <th className="text-left px-5 py-3">Category</th>
              <th className="text-right px-5 py-3">Variants · Price range</th>
              <th className="text-right px-5 py-3">Stock</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const active = (p.variants || []).filter((v) => v.active !== false);
              const prices = active.map((v) => Number(v.price || 0));
              const min = prices.length ? Math.min(...prices) : Number(p.price || 0);
              const max = prices.length ? Math.max(...prices) : Number(p.price || 0);
              const stockSum = active.reduce((s, v) => s + Number(v.inventory || 0), 0);
              return (
                <tr key={p.slug} className="border-b border-slate-100 last:border-b-0" data-testid={`admin-product-${p.slug}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                        {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover"/> : <span className="text-[9px] font-mono text-slate-400">—</span>}
                      </div>
                      <div>
                        <div className="text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{p.category}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="font-mono text-xs text-slate-500">{active.length} {active.length === 1 ? "variant" : "variants"}</div>
                    <div className="font-mono text-sm text-slate-900" data-testid={`admin-product-price-range-${p.slug}`}>
                      {min === max ? `$${min.toFixed(2)}` : `$${min.toFixed(2)} – $${max.toFixed(2)}`}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right font-mono">{stockSum || (p.inventory?.stock ?? "—")}</td>
                  <td className="px-5 py-4"><Badge className={`rounded-md font-mono text-[10px] uppercase tracking-widest border ${p.status === "active" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.status}</Badge></td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-slate-100" data-testid={`admin-product-edit-${p.slug}`}><Pencil className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75}/></button>
                    {p.status === "active" && <button onClick={() => archive(p.slug)} className="p-1.5 rounded-md hover:bg-red-50"><Archive className="w-3.5 h-3.5 text-red-500" strokeWidth={1.75}/></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? `Edit ${editing}` : "New product"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input required placeholder="Slug (e.g. bpc-157)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} disabled={!!editing}/>
              <Input required placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
              <Input placeholder="Chemical name" value={form.chemical_name} onChange={(e) => setForm({...form, chemical_name: e.target.value})}/>
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="h-10 px-3 rounded-md border border-slate-300 text-sm">
                <option>Recovery</option><option>Metabolic</option><option>Cognitive</option><option>Supplies</option>
              </select>
              <Input placeholder="Focus" value={form.focus} onChange={(e) => setForm({...form, focus: e.target.value})}/>
              <Input placeholder="Sequence" value={form.sequence} onChange={(e) => setForm({...form, sequence: e.target.value})}/>
              <Input placeholder="Purity" value={form.purity} onChange={(e) => setForm({...form, purity: e.target.value})}/>
              <Input placeholder="Form" value={form.form} onChange={(e) => setForm({...form, form: e.target.value})}/>
              <Input placeholder="Molecular weight" value={form.molecular_weight} onChange={(e) => setForm({...form, molecular_weight: e.target.value})}/>
            </div>

            {/* Variant Manager — replaces the old single-price field */}
            <VariantManager value={form.variants} onChange={(variants) => setForm({...form, variants})}/>

            {/* Product image */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Product Image</div>
              <div className="flex gap-3 items-start">
                <div className="w-24 h-24 rounded-md border border-slate-200 bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }}/>
                  ) : (
                    <span className="text-[10px] font-mono text-slate-400">No image</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input data-testid="product-image-url" placeholder="Image URL (https://…)" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})}/>
                  <p className="text-[11px] text-slate-500">Paste any public image URL. Recommended: 800×600, JPG or PNG.</p>
                </div>
              </div>
            </div>

            <Textarea rows={3} placeholder="Summary" value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})}/>
            <Textarea rows={3} placeholder="Benefits (one per line)" value={benefitsRaw} onChange={(e) => setBenefitsRaw(e.target.value)}/>
            <Textarea rows={2} placeholder="Dosage" value={form.dosage} onChange={(e) => setForm({...form, dosage: e.target.value})}/>
            <Textarea rows={2} placeholder="Storage" value={form.storage} onChange={(e) => setForm({...form, storage: e.target.value})}/>

            {/* Recommended products */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid="recommended-products">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Recommended products</div>
              <div className="text-[11px] text-slate-500 mb-2">Shown to customers on the checkout Add-ons panel and (later) on this product's detail page.</div>
              <div className="max-h-40 overflow-y-auto border border-slate-200 bg-white rounded p-2">
                {items.filter((p) => p.slug !== form.slug).map((p) => (
                  <label key={p.slug} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-slate-50 rounded">
                    <input
                      type="checkbox"
                      checked={(form.recommended_products || []).includes(p.slug)}
                      onChange={(e) => {
                        const cur = form.recommended_products || [];
                        setForm({...form, recommended_products: e.target.checked ? [...cur, p.slug] : cur.filter((s) => s !== p.slug)});
                      }}
                      data-testid={`recommend-${p.slug}`}
                    />
                    <span className="font-mono text-slate-500">{p.slug}</span>
                    <span className="text-slate-700">· {p.name}</span>
                    <span className="ml-auto text-[10px] text-slate-400 font-mono">{p.category}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})}/> Featured</label>
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="h-9 px-3 rounded-md border border-slate-300 text-sm">
                <option value="active">Active</option><option value="archived">Archived</option><option value="draft">Draft</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">{editing ? "Save" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
